/**
 * Bravo Assistant — seeds `useChat` from a tRPC-loaded conversation and sends
 * only the new message + conversation id (the server loads prior history). The
 * id is minted once per mount; the panel remounts (via `key`) to switch chats.
 */
import * as React from 'react';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { ChatMessage } from 'shared/src/api/ai-chat-types';
import type { AssistantConversation } from 'backend/src/ai/assistant-conversations';

const assistantTransport = new DefaultChatTransport<ChatMessage>({
  api: '/api/chat',
  // `body` carries per-send extras on top of the standard id + new-message contract.
  prepareSendMessagesRequest: ({ id, messages, body }) => ({
    body: { id, message: messages[messages.length - 1], ...body }
  })
});

export function useAssistantConversation(
  initialConversation: AssistantConversation | null,
  conversationIdOverride?: string
) {
  // Mint the id client-side for ordinary new chats; Wrkthru launches may pass a
  // server-reserved id, and existing conversations resume their persisted id.
  const [conversationId] = React.useState(
    () => initialConversation?.id ?? conversationIdOverride ?? crypto.randomUUID()
  );

  return useChat<ChatMessage>({
    id: conversationId,
    messages: initialConversation?.messages ?? [],
    transport: assistantTransport
  });
}
